import {Component} from "@angular/core";
import {VerbrauchsanalyseStepComponent} from "../verbrauchsanalyse/verbrauchsanalyse.step.component";
import {StepService} from "../../services/step.service";
import {Router} from "@angular/router";
import {ElementService} from "../../services/element.service";
import {ModeratRequestService} from "../services/request.service";
import {PortalService} from "../../services/portal.service";
import {EnergyMapService} from "../../energy-map/services/energy.map.service";

@Component({
  selector: 'app-moderat-fernwaerme-ergebnis',
  templateUrl: './fernwaerme.ergebnis.component.html',
  standalone: false
})

export class FernwaermeErgebnisComponent extends VerbrauchsanalyseStepComponent {

  /**
   * The only `result` values with their own texts/behaviour - anything else (unset, still
   * loading, or an unrecognized backend value) falls back to `SAFE_FALLBACK_RESULT` in
   * `updateTextkeyPostfix()`.
   */
  private static readonly KNOWN_RESULTS = ['ja', 'nein', 'perspektivisch', 'keine_adresse'];

  /**
   * Safe fallback for any `result` that isn't one of `KNOWN_RESULTS`. Must be 'nein', never
   * 'ja': 'ja' would show the "district heating available" call-to-action for a result that
   * was never actually confirmed (e.g. while still loading, or if the check request fails).
   */
  private static readonly SAFE_FALLBACK_RESULT = 'nein';

  /**
   * Textkey prefix for the used texts
   */
  textkeyPrefix: string = 'moderat.fernwaerme.ergebnis.';

  /**
   * Toggle for showing/hiding the logo in teaser
   */
  showDistrictTeaserLogo: boolean = false;

  /**
   * Textkey postfix for the used texts. Defaults to the same safe fallback as `result` - the
   * template doesn't render anything using it before `result` is set, but this avoids an
   * "undefined" concatenation in the (unused) meantime, e.g. for the step button's label.
   */
  textkeyPostfix: string = '.' + FernwaermeErgebnisComponent.SAFE_FALLBACK_RESULT;

  /**
   * result of the district heating check, used to control which texts/elements are shown.
   *
   * Starts as `null` (no result yet, the district heating status is still being fetched from
   * the backend) - the template only renders the result content once this is set to a real
   * value. This must NOT default to 'ja': that would briefly (or, if the request ever fails
   * silently, permanently) show the "district heating is available" page with its call-to-action
   * content before/without the actual check ever confirming it.
   */
  result: string = null;

  constructor(protected stepService: StepService, protected router: Router, protected requestService: ModeratRequestService, protected elementService: ElementService,
              protected portalService: PortalService, protected energyMapService: EnergyMapService) {
    super(stepService, router, requestService, elementService, portalService, energyMapService);
  }
  ngOnInit() {
    super.ngOnInit();

    // get the portal config to show the portal specific logo
    this.showDistrictTeaserLogo = this.portalService?.getConfigAsBool('fernwaerme.enable.teaser.showlogo');

    if (!this.anfrage?.verbrauchsanalyseInput?.adresse) {
      this.result = 'keine_adresse';
      this.updateTextkeyPostfix();
      this.saveFernwaermenetz('keine_adresse');
      return;
    }

    // send the address fields directly instead of relying on them already being persisted
    // server-side - the regular save-to-server call runs in the background (see
    // RequestService/BackendService.saveData()) and isn't awaited before moving to the next
    // step, so a database-only read here could race against that save and see stale data.
    const input = this.anfrage.verbrauchsanalyseInput;
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      this.requestService.backendService.districtHeatingStatus(requestId, {
        adresse_postalcode: input.adresse_postalcode,
        adresse_strasse: input.adresse_strasse,
        adresse_hausnummer: input.adresse_hausnummer,
      }).subscribe({
        next: ({status}) => {
          this.result = status;
          this.updateTextkeyPostfix();
          // 'ja' is the only status that means district heating is actually present -
          // 'nein' and 'perspektivisch' both map to 'nicht_vorhanden' for now, since the
          // building-condition API doesn't accept 'perspektivisch' yet
          this.saveFernwaermenetz(status);
        },
        // if the check itself fails, fall back to the safe outcome ('nein' hides the
        // call-to-action content) instead of leaving the page stuck on no result at all
        error: () => {
          this.result = FernwaermeErgebnisComponent.SAFE_FALLBACK_RESULT;
          this.updateTextkeyPostfix();
          this.saveFernwaermenetz('nein');
        }
      });
    });
  }

  /**
   * Persists the district heating network status onto verbrauchsanalyseInput, so the backend
   * can forward it as 'fernwaermenetz' to the building-condition API (see
   * ModeratController::buildRequestDataForBuildingConditionsApi()).
   */
  saveFernwaermenetz(fernwaermenetz: string): void {
    this.saveData({fernwaermenetz});
  }

  /**
   * Update the textkey postfix for the district heating result texts - just `result` with a
   * leading dot, e.g. 'ja' -> '.ja'. Falls back to `SAFE_FALLBACK_RESULT` for anything outside
   * `KNOWN_RESULTS` (unset, still loading, or an unrecognized backend value).
   */
  updateTextkeyPostfix(): void {
    const knownResult = FernwaermeErgebnisComponent.KNOWN_RESULTS.includes(this.result);
    let result = knownResult ? this.result : FernwaermeErgebnisComponent.SAFE_FALLBACK_RESULT;
    if (knownResult && result.includes('_')) {
      result = result.replace('_', '.');
    }
    this.textkeyPostfix = '.' + (result);
  }

}
